import { useEffect, useState } from "react";
import {
  getScheduleUpdates,
  markScheduleUpdateRead,
  markAllScheduleUpdatesRead,
} from "../../../../services/schedule-updates";
import type { ScheduleUpdateNotification } from "../../../../types/schedule-updates";
import { describeScheduleUpdate } from "./scheduleUpdateSummary";

/** True while the notification has not been read (server-authoritative). */
function isUnread(n: ScheduleUpdateNotification): boolean {
  return n.readAt == null;
}

/**
 * Short, human date for a notification row, e.g. "Jul 16" or "Jul 16, 2025"
 * once it is in a prior year. Plain and compact, no relative fuzz to keep it
 * unambiguous across days. Returns "" for an unparseable timestamp so the row
 * still renders without a broken date.
 */
function formatShortDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const opts: Intl.DateTimeFormatOptions =
    date.getFullYear() === now.getFullYear()
      ? { month: "short", day: "numeric" }
      : { month: "short", day: "numeric", year: "numeric" };
  return date.toLocaleDateString(undefined, opts);
}

/** Newest-first by createdAt; ties keep input order (stable sort). */
function newestFirst(
  a: ScheduleUpdateNotification,
  b: ScheduleUpdateNotification
): number {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

interface ScheduleUpdatesListProps {
  /**
   * Ids of the viewer's distributed copies that still need setup. When a
   * notification's event is in this set, the row offers a "Set up your itinerary"
   * CTA. The notification payload itself does not carry needsSetup, so the parent
   * (which already holds the loaded events) supplies it — no new API. When
   * omitted, no CTA is shown and the rows behave exactly as before.
   */
  needsSetupEventIds?: ReadonlySet<string>;
  /** Called with the event id when the "Set up your itinerary" CTA is used. */
  onSetUp?: (eventId: string) => void;
}

/**
 * A minimal feed of the caller's schedule-update notifications. Fetches all
 * notifications (read and unread), lists them newest-first with unread rows
 * visually distinct, and lets the user mark a single row (by clicking it) or
 * every unread row read. Seen-state is server-authoritative: each action posts
 * to the server and then reflects the returned/known state locally.
 *
 * When a notification is about a distributed copy the viewer still needs to set
 * up (via `needsSetupEventIds`), the row also offers a "Set up your itinerary"
 * CTA that routes into the Build Itinerary wizard.
 *
 * Fetch is guarded against the set-state-after-await race with a mounted flag
 * and an AbortController so a resolved fetch never writes into an unmounted
 * render.
 */
export const ScheduleUpdatesList = ({
  needsSetupEventIds,
  onSetUp,
}: ScheduleUpdatesListProps = {}) => {
  const [items, setItems] = useState<ScheduleUpdateNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    setLoading(true);
    setError(false);

    getScheduleUpdates(false, controller.signal)
      .then((updates) => {
        if (!active) return;
        setItems([...updates].sort(newestFirst));
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  const unreadCount = items.filter(isUnread).length;

  const handleRowClick = (n: ScheduleUpdateNotification) => {
    if (!isUnread(n)) return;
    // Optimistically mark read locally so the row updates immediately; the
    // server is authoritative, so a failure just leaves it unread on the next
    // load. We stamp a local readAt to flip the unread styling right away.
    const readAt = new Date().toISOString();
    setItems((prev) =>
      prev.map((item) => (item.id === n.id ? { ...item, readAt } : item))
    );
    markScheduleUpdateRead(n.id).catch(() => {
      /* non-fatal: server stays source of truth; resurfaces next load */
    });
  };

  const handleMarkAll = () => {
    const readAt = new Date().toISOString();
    setItems((prev) =>
      prev.map((item) => (isUnread(item) ? { ...item, readAt } : item))
    );
    markAllScheduleUpdatesRead().catch(() => {
      /* non-fatal: server stays source of truth; resurfaces next load */
    });
  };

  return (
    <div className="schedule-updates-list" data-testid="schedule-updates-list">
      <div className="schedule-updates-list__header">
        <span className="schedule-updates-list__title">
          Updates
          <span
            className="schedule-updates-list__count-live"
            aria-live="polite"
            aria-atomic="true"
          >
            {unreadCount > 0 && (
              <span
                className="schedule-updates-list__count"
                aria-label={`${unreadCount} unread`}
                data-testid="schedule-updates-unread-count"
              >
                {unreadCount}
              </span>
            )}
          </span>
        </span>
        {unreadCount > 0 && (
          <button
            type="button"
            className="schedule-updates-list__mark-all"
            onClick={handleMarkAll}
            data-testid="schedule-updates-mark-all"
          >
            Mark all read
          </button>
        )}
      </div>

      {loading ? (
        <p className="schedule-updates-list__empty">Loading updates...</p>
      ) : error ? (
        <p className="schedule-updates-list__empty" role="alert">
          Couldn't load updates. Try refreshing.
        </p>
      ) : items.length === 0 ? (
        <p
          className="schedule-updates-list__empty"
          data-testid="schedule-updates-empty"
        >
          No schedule updates.
        </p>
      ) : (
        <ul className="schedule-updates-list__items">
          {items.map((n) => {
            const unread = isUnread(n);
            const summary = describeScheduleUpdate(n);
            const date = formatShortDate(n.createdAt);
            const showSetUp =
              onSetUp != null &&
              needsSetupEventIds != null &&
              needsSetupEventIds.has(n.calendarEventId);
            return (
              <li key={n.id}>
                <button
                  type="button"
                  className={`schedule-updates-row${unread ? " schedule-updates-row--unread" : ""}`}
                  onClick={() => handleRowClick(n)}
                  aria-label={`${n.eventTitle ?? "Schedule"}${unread ? ", unread" : ""}`}
                  data-testid={`schedule-updates-row-${n.id}`}
                >
                  {unread && (
                    <span
                      className="schedule-updates-row__unread-dot"
                      aria-hidden="true"
                      data-testid={`schedule-updates-row-unread-${n.id}`}
                    />
                  )}
                  <span className="schedule-updates-row__body">
                    <span className="schedule-updates-row__title">
                      {n.eventTitle ?? "Schedule"}
                    </span>
                    {summary && (
                      <span className="schedule-updates-row__summary">
                        {summary}
                      </span>
                    )}
                  </span>
                  {date && (
                    <span className="schedule-updates-row__date">{date}</span>
                  )}
                </button>
                {showSetUp && (
                  <button
                    type="button"
                    className="schedule-updates-row__setup"
                    onClick={() => onSetUp?.(n.calendarEventId)}
                    data-testid={`schedule-updates-setup-${n.id}`}
                  >
                    Set up your itinerary
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
